import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Card,
  CardContent,
  Collapse,
  Tooltip,
  Fab,
  Badge,
  Slide,
  Zoom
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  ClearAll as ClearAllIcon,
  Minimize as MinimizeIcon
} from '@mui/icons-material';
import { OrderAlert } from '../../hooks/useOrderAlertsSSE';
import { format } from 'date-fns';

// Alert sound
const criticalAlertSound = new Audio(require('../../assets/alert_simple.wav'));
criticalAlertSound.volume = 0.7;

interface OrderAlertsPopupProps {
  alerts: OrderAlert[];
  isConnected: boolean;
  error: Error | null;
  onDismissAlert: (alertId: string) => void;
  onClearAll: () => void;
  onAlertClick?: (orderId: string) => void;
}

interface AlertSwimlane {
  priority: number;
  label: string;
  color: string;
  bgColor: string;
  alerts: OrderAlert[];
}

export const OrderAlertsPopup: React.FC<OrderAlertsPopupProps> = ({
  alerts,
  isConnected,
  error,
  onDismissAlert,
  onClearAll,
  onAlertClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedPriorities, setExpandedPriorities] = useState<Set<number>>(
    new Set([5, 4]) // Critical and High expanded by default
  );
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [hasNewAlerts, setHasNewAlerts] = useState(false);
  const previousAlertIds = useRef<Set<string>>(new Set());
  const previousAlertCount = useRef(0);

  // Play sound for new critical alerts and pulse the button
  useEffect(() => {
    const newCriticalAlerts = alerts.filter(
      alert => alert.priority >= 4 && !previousAlertIds.current.has(alert.alertId)
    );

    if (newCriticalAlerts.length > 0) {
      setHasNewAlerts(true);
      
      if (soundEnabled) {
        try {
          criticalAlertSound.play();
        } catch (error) {
          // Ignore - user might not have interacted with page yet
        }
      }
    }

    // Check if new alerts arrived
    if (alerts.length > previousAlertCount.current && !isOpen) {
      setHasNewAlerts(true);
    }

    // Update previous alert IDs and count
    previousAlertIds.current = new Set(alerts.map(a => a.alertId));
    previousAlertCount.current = alerts.length;
  }, [alerts, soundEnabled, isOpen]);

  // Clear new alerts indicator when opened
  useEffect(() => {
    if (isOpen) {
      setHasNewAlerts(false);
    }
  }, [isOpen]);

  // Group alerts by priority into swimlanes
  const swimlanes: AlertSwimlane[] = useMemo(() => {
    const priorityConfig = [
      { priority: 5, label: 'Critical', color: '#d32f2f', bgColor: '#ffebee' },
      { priority: 4, label: 'High', color: '#f57c00', bgColor: '#fff3e0' },
      { priority: 3, label: 'Medium', color: '#fbc02d', bgColor: '#fffde7' },
      { priority: 2, label: 'Low', color: '#388e3c', bgColor: '#e8f5e9' },
      { priority: 1, label: 'Info', color: '#1976d2', bgColor: '#e3f2fd' }
    ];

    return priorityConfig.map(config => ({
      ...config,
      alerts: alerts.filter(alert => alert.priority === config.priority)
    })).filter(lane => lane.alerts.length > 0);
  }, [alerts]);

  const togglePriority = (priority: number) => {
    setExpandedPriorities(prev => {
      const newSet = new Set(prev);
      if (newSet.has(priority)) {
        newSet.delete(priority);
      } else {
        newSet.add(priority);
      }
      return newSet;
    });
  };

  const getAlertTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'NO_COLLECTOR_ASSIGNED': 'No Collector',
      'LANDING_PAD_UNASSIGNED': 'Pad Unassigned',
      'TAKEOFF_OVERDUE': 'Takeoff Overdue',
      'STATUS_CHANGE': 'Status Change',
      'COLLECTOR_STATUS_CHANGE': 'Collector Status',
      'READY_FOR_DISPATCH': 'Ready',
      'ORDER_CANCELLED': 'Cancelled',
      'DISPATCHED': 'Dispatched',
      'DELIVERED': 'Delivered',
      'REQUIRES_ATTENTION': 'Attention',
      'STATE_TIMEOUT': 'Timeout'
    };
    return labels[type] || type;
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return format(date, 'HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const totalAlerts = alerts.length;
  const criticalCount = alerts.filter(a => a.priority === 5).length;
  const highCount = alerts.filter(a => a.priority === 4).length;

  const handleFabClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setHasNewAlerts(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Action Button - Intercom Style */}
      <Zoom in={true}>
        <Fab
          color="primary"
          aria-label="order alerts"
          onClick={handleFabClick}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 64,
            height: 64,
            zIndex: 1300,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            animation: hasNewAlerts ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              },
              '50%': {
                boxShadow: '0 4px 20px rgba(211, 47, 47, 0.6), 0 0 0 10px rgba(211, 47, 47, 0.1)'
              },
              '100%': {
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }
            },
            '&:hover': {
              transform: 'scale(1.1)',
              transition: 'transform 0.2s'
            }
          }}
        >
          <Badge
            badgeContent={totalAlerts}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.9rem',
                height: 24,
                minWidth: 24,
                fontWeight: 'bold'
              }
            }}
          >
            <NotificationsIcon sx={{ fontSize: 32 }} />
          </Badge>
        </Fab>
      </Zoom>

      {/* Popup Window - Intercom Style */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 100,
            right: 24,
            width: 420,
            maxWidth: 'calc(100vw - 48px)',
            height: 600,
            maxHeight: 'calc(100vh - 140px)',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1300,
            boxShadow: '0 12px 40px rgba(0,0,0,0.4)'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Order Alerts
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={isConnected ? 'Live' : 'Offline'}
                    size="small"
                    sx={{
                      bgcolor: isConnected ? 'rgba(76, 175, 80, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                      color: 'white',
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  />
                  {criticalCount > 0 && (
                    <Chip
                      label={`${criticalCount} Critical`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(211, 47, 47, 0.9)',
                        color: 'white',
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    />
                  )}
                  {highCount > 0 && (
                    <Chip
                      label={`${highCount} High`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(245, 124, 0, 0.9)',
                        color: 'white',
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title={soundEnabled ? 'Disable sounds' : 'Enable sounds'}>
                <IconButton
                  size="small"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  sx={{ color: 'white' }}
                >
                  {soundEnabled ? <NotificationsIcon /> : <NotificationsOffIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{ color: 'white' }}
                >
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Error Banner */}
          {error && (
            <Box sx={{ p: 1.5, bgcolor: '#f44336', color: 'white' }}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                ⚠️ Connection Error: {error.message}
              </Typography>
            </Box>
          )}

          {/* Action Bar */}
          {totalAlerts > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                bgcolor: '#f5f5f5',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {totalAlerts} {totalAlerts === 1 ? 'alert' : 'alerts'}
              </Typography>
              <Tooltip title="Clear all alerts">
                <IconButton
                  size="small"
                  onClick={onClearAll}
                  color="primary"
                >
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Alerts Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              bgcolor: '#fafafa',
              '&::-webkit-scrollbar': {
                width: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1'
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bbb',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#999'
              }
            }}
          >
            {totalAlerts === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 3,
                  color: 'text.secondary'
                }}
              >
                <NotificationsOffIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                  All clear!
                </Typography>
                <Typography variant="body2" align="center">
                  {isConnected ? 'No alerts at the moment. We\'ll notify you when something needs attention.' : 'Connecting to alerts service...'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                {swimlanes.map(lane => (
                  <Box key={lane.priority} sx={{ mb: 2 }}>
                    {/* Swimlane Header */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: lane.bgColor,
                        borderLeft: 4,
                        borderColor: lane.color,
                        cursor: 'pointer',
                        borderRadius: 1,
                        mb: 1,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        '&:hover': {
                          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                        }
                      }}
                      onClick={() => togglePriority(lane.priority)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ color: lane.color }}
                        >
                          {lane.label}
                        </Typography>
                        <Chip
                          label={lane.alerts.length}
                          size="small"
                          sx={{
                            bgcolor: lane.color,
                            color: 'white',
                            fontWeight: 'bold',
                            height: 22
                          }}
                        />
                      </Box>
                      <IconButton size="small" sx={{ color: lane.color }}>
                        {expandedPriorities.has(lane.priority) ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Box>

                    {/* Swimlane Content */}
                    <Collapse in={expandedPriorities.has(lane.priority)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {lane.alerts.map(alert => (
                          <Card
                            key={alert.alertId}
                            sx={{
                              borderLeft: 3,
                              borderColor: lane.color,
                              cursor: onAlertClick ? 'pointer' : 'default',
                              transition: 'all 0.2s',
                              '&:hover': onAlertClick
                                ? {
                                    transform: 'translateX(4px)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                  }
                                : {}
                            }}
                            onClick={() => {
                              onAlertClick?.(alert.orderId);
                              handleClose();
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 0.5
                                }}
                              >
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1 }}>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    #{alert.orderShortId}
                                  </Typography>
                                  <Chip
                                    label={getAlertTypeLabel(alert.alertType)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    {formatTimestamp(alert.timestamp)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDismissAlert(alert.alertId);
                                    }}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                                {alert.message}
                              </Typography>

                              {/* Additional metadata */}
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {alert.currentState && (
                                  <Chip
                                    label={alert.currentState}
                                    size="small"
                                    variant="filled"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                {alert.metadata?.minutesUntilDelivery !== undefined && (
                                  <Chip
                                    label={`${alert.metadata.minutesUntilDelivery}min to delivery`}
                                    size="small"
                                    color="warning"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                                {alert.metadata?.minutesPastTakeoff !== undefined && (
                                  <Chip
                                    label={`${alert.metadata.minutesPastTakeoff}min overdue`}
                                    size="small"
                                    color="error"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Footer - Branding/Info */}
          <Box
            sx={{
              p: 1,
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Powered by Manna Order Alerts
            </Typography>
          </Box>
        </Paper>
      </Slide>
    </>
  );
};

